import { useState, useEffect } from 'react';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import { formatToHumanDateTime } from '../utils/DateFormatter';
import { RecipientGroupCard, RecipientModal } from './RecipientPicker';


function DashboardAdvisory({
    setActiveAdvisory, countryId, speciesImgCode, speciesObj, activeAdvisory, getAuthHeaders, handleApiError,
    weekly = false, setActiveWeeklyAdvisory,
}) {
    const [advisoryText, setAdvisoryText] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [localLanguageLabel, setLocalLanguageLabel] = useState(activeAdvisory.local_language || '');
    const [sending, setSending] = useState(false);
    const [farmersList, setFarmersList] = useState([]);
    const [officersList, setOfficersList] = useState([]);
    const [farmersActive, setFarmersActive] = useState(true);
    const [farmersMode, setFarmersMode] = useState("all");
    const [farmersSelected, setFarmersSelected] = useState(new Set());
    const [officersActive, setOfficersActive] = useState(true);
    const [officersMode, setOfficersMode] = useState("all");
    const [officersSelected, setOfficersSelected] = useState(new Set());
    const [modalOpen, setModalOpen] = useState(null);

    const fmtDate = d => { const dt = new Date(d); return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`; };

    const weeklyPrefix = () => {
        if (!weekly || !activeAdvisory.week_start) return '';
        return `Weekly Advisory for ${fmtDate(activeAdvisory.week_start)} – ${fmtDate(activeAdvisory.week_end)}\n`;
    };

    const dailyPrefix = language => {
        const date = new Date(activeAdvisory.ref_date);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;
        if (language === 'English') return `Advisory for ${formattedDate}\n`;
        if (language === 'Bemba') return `Ifunde ya ${formattedDate}\n`;
        return `Langizo la ${formattedDate}\n`;
    };

    const getPrefix = language => weekly ? weeklyPrefix() : dailyPrefix(language);

    const getBodyText = language => {
        if (language === "English")
            return activeAdvisory.advisory_text?.map(line => line.trim()).join("\n") || "";
        if (language === "Chichewa" && countryId === 2)
            return activeAdvisory.local_text2?.map(line => line.trim()).join("\n") || "";
        return activeAdvisory.local_text?.map(line => line.trim()).join("\n") || "";
    };

    const handleLanguageChange = language => {
        setSelectedLanguage(language);
        setAdvisoryText(getPrefix(language) + getBodyText(language));
    };

    useEffect(() => {
        if (activeAdvisory) {
            setAdvisoryText(getPrefix(selectedLanguage) + getBodyText(selectedLanguage));
            setLocalLanguageLabel(activeAdvisory.local_language || '');
        }
    }, [activeAdvisory, selectedLanguage]);

    useEffect(() => {
        if (!countryId || !activeAdvisory?.adm3_id) return;
        const headers = getAuthHeaders();
        Promise.all([
            fetch(`${envConfig.API_URL}/fishermen?country_id=${countryId}&adm3_id=${activeAdvisory.adm3_id}`, { method: 'GET', headers }),
            fetch(`${envConfig.API_URL}/extoff?country_id=${countryId}&adm3_id=${activeAdvisory.adm3_id}`, { method: 'GET', headers }),
        ])
            .then(([fRes, oRes]) => Promise.all([fRes.json().catch(() => ({})), oRes.json().catch(() => ({}))]))
            .then(([fData, oData]) => {
                setFarmersList((fData.data || []).map(f => ({ id: f.fisher_id, name: f.name, phone: f.phone })));
                setOfficersList((oData.data || []).map(o => ({ id: o.fisher_id, name: o.name, phone: o.phone, role: o.designation || "Extension Officer" })));
            })
            .catch(() => { setFarmersList([]); setOfficersList([]); });
        setFarmersSelected(new Set()); setFarmersMode("all"); setFarmersActive(true);
        setOfficersSelected(new Set()); setOfficersMode("all"); setOfficersActive(true);
    }, [activeAdvisory?.adm3_id]);

    const toggleId = (setFn, current, id) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        setFn(next);
    };

    const totalRecipients =
        (farmersActive ? (farmersMode === "all" ? farmersList.length : farmersSelected.size) : 0) +
        (officersActive ? (officersMode === "all" ? officersList.length : officersSelected.size) : 0);

    const sendMessage = () => {
        let reqBody = {
            "country_id": countryId,
            "adm3_id": activeAdvisory.adm3_id,
            "adm2_id": activeAdvisory.adm2_id,
            "adm1_id": activeAdvisory.adm1_id,
            "advisory_text": advisoryText,
            "risk_level": activeAdvisory.risk_level,
            "farmers_active": farmersActive,
            "extoff_active": officersActive,
            "farmer_ids": farmersMode === "select" && farmersSelected.size > 0 ? [...farmersSelected] : null,
            "extoff_ids": officersMode === "select" && officersSelected.size > 0 ? [...officersSelected] : null,
        };
        if (weekly) {
            reqBody.weekly = true;
            reqBody.week_start = activeAdvisory.week_start;
            reqBody.week_end = activeAdvisory.week_end;
            reqBody.week_pond_temp_min = activeAdvisory.week_pond_temp_min;
            reqBody.week_pond_temp_max = activeAdvisory.week_pond_temp_max;
        } else {
            reqBody.predicted_pond_temperature = activeAdvisory.predicted_pond_temperature;
            reqBody.temp_max = activeAdvisory.temp_max;
            reqBody.temp_min = activeAdvisory.temp_min;
            reqBody.ref_date = activeAdvisory.ref_date;
        }
        setSending(true);
        fetch(
            `${envConfig.API_URL}/advisory/broadcast`,
            { 'method': "POST", 'headers': getAuthHeaders(), "body": JSON.stringify(reqBody) },
        )
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const error = new Error(data.message || `HTTP error! status: ${response.status}`);
                    throw { error, response };
                }
                return data;
            })
            .then(response => {
                Swal.fire({ title: "Success!", text: response.message, icon: "success", confirmButtonText: "OK" });
            })
            .catch(({ error, response }) => {
                const errorMessage = handleApiError(error, response);
                console.error("Error fetching data:", errorMessage);
                Swal.fire({ title: "Error!", text: errorMessage || "Unable to call the broadcast API", icon: "error", confirmButtonText: "OK" });
            })
            .finally(() => { setSending(false); });
    };

    const closePanel = () => {
        if (weekly && setActiveWeeklyAdvisory) setActiveWeeklyAdvisory(null);
        else setActiveAdvisory(null);
    };

    const headerLabel = weekly
        ? `Weekly Advisory for ${fmtDate(activeAdvisory.week_start)} – ${fmtDate(activeAdvisory.week_end)}`
        : `Advisory for ${activeAdvisory.ref_date}`;

    return (<>
        <div className="p-2">
            <div className="d-flex justify-content-between align-items-center mb-2" style={{ position: "relative" }} >
                <b className="m-0">{headerLabel}</b>
                <button
                    type="button"
                    aria-label="Close"
                    onClick={closePanel}
                    style={{
                        background: "transparent", border: "none", fontSize: "22px",
                        fontWeight: "bold", lineHeight: "1", cursor: "pointer", color: "#000",
                    }}
                >&times;</button>
            </div>
            <div className="row align-items-center">
                {!weekly && (
                    <div className="col-sm-12 col-md-2 col-lg-2">
                        <img
                            src={`assets/images/species-${speciesImgCode}.jpg`}
                            className="img-fluid rounded"
                            style={{ maxHeight: "155px", objectFit: "cover" }}
                            loading="lazy"
                            alt="Species"
                        />
                    </div>
                )}
                {!weekly && (
                    <div className="col-sm-12 col-md-2 col-lg-2">
                        <div className="text-lg-center mb-3 mb-md-0 px-0">
                            <h6 className="fw-bold mt-1">Species</h6>
                            <span className="mb-1 mt-2 pt-1">{speciesObj?.scientific_name}</span>
                            <p className="" style={{ color: '#1A4985', fontWeight: '500' }}>({speciesObj?.common_name})</p>
                        </div>
                    </div>
                )}
                <div className={weekly ? "col-sm-12 col-md-12 col-lg-12" : "col-sm-12 col-md-8 col-lg-8"}>
                    <div className="row text-lg-center mb-3 mb-md-0 px-0">
                        <div className="col-sm-12 col-md-3 col-lg-3">
                            <h6 className="fw-bold">Risk Level</h6>
                            <span className="mb-1 mt-2 pt-1">{activeAdvisory.risk_level}</span>
                        </div>
                        {weekly ? (<>
                            <div className="col-sm-12 col-md-3 col-lg-3">
                                <h6 className="fw-bold">Week Pond Temp Min</h6>
                                <span className="mb-1 mt-2 pt-1">{activeAdvisory.week_pond_temp_min ?? '--'} °C</span>
                            </div>
                            <div className="col-sm-12 col-md-3 col-lg-3">
                                <h6 className="fw-bold">Week Pond Temp Max</h6>
                                <span className="mb-1 mt-2 pt-1">{activeAdvisory.week_pond_temp_max ?? '--'} °C</span>
                            </div>
                        </>) : (<>
                            <div className="col-sm-12 col-md-3 col-lg-3">
                                <h6 className="fw-bold">Predicted Pond Temperature</h6>
                                <span className="mb-1 mt-2 pt-1">{activeAdvisory.predicted_pond_temperature} °C</span>
                            </div>
                            <div className="col-sm-12 col-md-3 col-lg-3">
                                <h6 className="fw-bold">Max. Air Temperature</h6>
                                <span className="mb-1 mt-2 pt-1">{activeAdvisory.temp_max} °C</span>
                            </div>
                            <div className="col-sm-12 col-md-3 col-lg-3">
                                <h6 className="fw-bold">Min. Air Temperature</h6>
                                <span className="mb-1 mt-2 pt-1">{activeAdvisory.temp_min} °C</span>
                            </div>
                        </>)}
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12 mt-3">
                    {/* Language Selection Radio Buttons */}
                    <div className="d-flex mb-3">
                        <div className="form-check me-3">
                            <input type="radio" className="form-check-input" id="da-english" name="da-language" value="English"
                                checked={selectedLanguage === 'English'} onChange={() => handleLanguageChange('English')} />
                            <label className="form-check-label" htmlFor="da-english">English</label>
                        </div>
                        <div className="form-check me-3">
                            <input type="radio" className="form-check-input" id="da-localLanguage" name="da-language" value={localLanguageLabel}
                                checked={selectedLanguage === localLanguageLabel} onChange={() => handleLanguageChange(localLanguageLabel)} />
                            <label className="form-check-label" htmlFor="da-localLanguage">{localLanguageLabel}</label>
                        </div>
                        {countryId === 2 && (
                            <div className="form-check">
                                <input type="radio" className="form-check-input" id="da-chichewa" name="da-language" value="Chichewa"
                                    checked={selectedLanguage === 'Chichewa'} onChange={() => handleLanguageChange('Chichewa')} />
                                <label className="form-check-label" htmlFor="da-chichewa">Chichewa</label>
                            </div>
                        )}
                    </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                    <div className="pe-3 w-100"><p className="mb-0">You may modify/rewrite the advisory before sending.</p></div>
                    <div>
                        <button className="btn btn-send" onClick={sendMessage} disabled={!advisoryText || sending}>
                            {sending ? "Sending..." : "Send"}
                        </button>
                    </div>
                </div>
                <div className="col-sm-12 col-md-12 col-lg-12 mt-3">
                    <textarea
                        className="centerText"
                        style={{
                            whiteSpace: 'pre-line', padding: '15px', width: '100%',
                            resize: 'none', background: 'white', fontSize: '14px', fontWeight: '400',
                        }}
                        rows={8}
                        value={advisoryText || ''}
                        onChange={e => setAdvisoryText(e.target.value)}
                    />
                </div>
                <div className="mt-3">
                    <p className="mb-2" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7686' }}>Recipients</p>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                        <RecipientGroupCard
                            icon="🐟" title="Fish Farmers" subtitle="Registered pond operators in this camp"
                            mode={farmersMode} setMode={setFarmersMode}
                            count={farmersSelected.size} total={farmersList.length}
                            active={farmersActive} setActive={setFarmersActive}
                            onOpenPicker={() => setModalOpen("farmers")}
                        />
                        <RecipientGroupCard
                            icon="🧑‍🌾" title="Extension Officers" subtitle="District & field support staff"
                            mode={officersMode} setMode={setOfficersMode}
                            count={officersSelected.size} total={officersList.length}
                            active={officersActive} setActive={setOfficersActive}
                            onOpenPicker={() => setModalOpen("officers")}
                        />
                    </div>
                    <p className="mt-2 mb-0" style={{ fontSize: 12.5, color: '#6B7686' }}>
                        <strong>{totalRecipients}</strong> recipient{totalRecipients !== 1 ? 's' : ''} will receive this advisory
                    </p>
                </div>
            </div>
        </div>
        {modalOpen === "farmers" && (
            <RecipientModal
                title="Select Fish Farmers" people={farmersList} fields="farmers"
                selectedIds={farmersSelected}
                onToggle={(id) => toggleId(setFarmersSelected, farmersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => farmersSelected.has(id));
                    setFarmersSelected(allSelected ? new Set() : new Set([...farmersSelected, ...ids]));
                }}
                onClear={() => setFarmersSelected(new Set())}
                onClose={() => setModalOpen(null)}
                onDone={() => setModalOpen(null)}
            />
        )}
        {modalOpen === "officers" && (
            <RecipientModal
                title="Select Extension Officers" people={officersList} fields="officers"
                selectedIds={officersSelected}
                onToggle={(id) => toggleId(setOfficersSelected, officersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => officersSelected.has(id));
                    setOfficersSelected(allSelected ? new Set() : new Set([...officersSelected, ...ids]));
                }}
                onClear={() => setOfficersSelected(new Set())}
                onClose={() => setModalOpen(null)}
                onDone={() => setModalOpen(null)}
            />
        )}
    </>)
}


export default DashboardAdvisory;
